import { useEffect, useRef, useState } from "react";
import { ChevronDown, PlayCircle, StopCircle, Store } from "lucide-react";
import { Store as StoreType } from "../../services/store.service";
import { Counter } from "../../services/counter.service";

interface StoreCounterSelectorProps {
  isBusinessOwner: boolean;
  availableStores: StoreType[];
  selectedStoreId: string | null;
  handleStoreChange: (id: string) => void;
  counters: Counter[];
  selectedCounterId: string | null;
  handleCounterChange: (id: string) => void;
  activeSession: any;
  onOpenSession: () => void;
  onCloseSession: () => void;
}

export function StoreCounterSelector({
  isBusinessOwner,
  availableStores,
  selectedStoreId,
  handleStoreChange,
  counters,
  selectedCounterId,
  handleCounterChange,
  activeSession,
  onOpenSession,
  onCloseSession,
}: StoreCounterSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentStore = availableStores.find(
    (store) => store.id === selectedStoreId,
  );
  const currentCounter = counters.find(
    (counter) => counter.id === selectedCounterId,
  );
  const canSelectStore = isBusinessOwner || availableStores.length > 1;

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div className="hidden shrink-0 items-center gap-2 md:flex">
      <div ref={containerRef} className="relative">
        <button
          type="button"
          aria-label={`Location: ${currentStore?.name || "Select store"}, ${currentCounter?.name || "Select counter"}`}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          onClick={() => setIsOpen((open) => !open)}
          className={`flex h-10 items-center gap-2 rounded-xl border px-2.5 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 xl:min-w-[210px] xl:px-3 ${
            isOpen
              ? "border-teal-200 bg-teal-50/70 text-teal-800"
              : "border-gray-200 bg-white text-gray-700 hover:border-teal-200 hover:bg-gray-50"
          }`}
        >
          <Store className="h-4 w-4 shrink-0 text-teal-600" />
          <span className="hidden min-w-0 flex-1 xl:block">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Location
            </span>
            <span className="block max-w-[165px] truncate text-xs font-medium text-slate-800">
              {currentStore?.name || "Select store"} ·{" "}
              {currentCounter?.name || "Select counter"}
            </span>
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div
            role="dialog"
            aria-label="Choose store and counter"
            className="absolute right-0 top-full z-[90] mt-3 w-72 rounded-2xl border border-gray-100 bg-white p-4 shadow-xl shadow-slate-900/10"
          >
            <div className="mb-4 flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                <Store className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Store &amp; counter
                </p>
                <p className="text-xs text-gray-500">
                  Billing location for this session
                </p>
              </div>
            </div>

            {canSelectStore ? (
              <label className="mb-3 block">
                <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Store
                </span>
                <select
                  value={selectedStoreId || ""}
                  onChange={(event) => handleStoreChange(event.target.value)}
                  className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
                >
                  <option value="" disabled>
                    Select store
                  </option>
                  {availableStores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div className="mb-3">
                <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  Store
                </span>
                <div className="flex h-10 items-center rounded-xl border border-gray-100 bg-gray-50 px-3 text-sm font-medium text-slate-700">
                  {currentStore?.name || "No store selected"}
                </div>
              </div>
            )}

            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Counter
              </span>
              <select
                value={selectedCounterId || ""}
                onChange={(event) => {
                  handleCounterChange(event.target.value);
                  setIsOpen(false);
                }}
                className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
              >
                <option value="" disabled>
                  Select counter
                </option>
                {counters.map((counter) => (
                  <option key={counter.id} value={counter.id}>
                    {counter.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {activeSession ? (
        <button
          type="button"
          onClick={onCloseSession}
          aria-label="Session active. Close session"
          className="flex h-10 items-center gap-2 rounded-full bg-emerald-50 px-3 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          title="Session active — click to close"
        >
          <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
          <span className="hidden lg:inline">Session active</span>
          <StopCircle className="h-3.5 w-3.5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpenSession}
          aria-label="No active session. Open session"
          className="flex h-10 items-center gap-2 rounded-full bg-amber-50 px-3 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          title="No active session — click to open"
        >
          <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
          <span className="hidden lg:inline">No session</span>
          <PlayCircle className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
